import React, { useState, useEffect, useRef } from "react";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";
import { Check, User, Building2 } from "lucide-react";

/**
 * ClientCombobox - Autocomplete input for client selection
 * - Type to search/filter existing clients
 * - Select from dropdown or type new name
 * - Returns both client_id (if selected) and client_name
 */
const ClientCombobox = ({ 
  clients = [], // Array of {id, name} from sales_clients
  value = "", // Current client_name value
  selectedId = null, // Current client_id if selected from list
  onChange, // (client_name, client_id) => void
  placeholder = "Start typing client name...",
  className = ""
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  // Filter clients based on input
  const filteredClients = clients.filter(client =>
    client.name?.toLowerCase().includes(inputValue.toLowerCase())
  ).slice(0, 8); // Limit to 8 suggestions

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setOpen(newValue.length > 0);
    // When typing, clear the selectedId (user is entering custom name)
    onChange(newValue, null);
  };

  // Handle selecting a client from dropdown
  const handleSelect = (client) => {
    setInputValue(client.name);
    onChange(client.name, client.id);
    setOpen(false);
    inputRef.current?.blur();
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const isSelected = selectedId && clients.some(c => c.id === selectedId);

  return (
    <div className={cn("relative", className)}>
      <div className="relative">
        <Input
          ref={inputRef}
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => inputValue.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className={cn(
            "bg-zinc-800 border-zinc-700 text-zinc-50 pr-8",
            isSelected && "border-green-600"
          )}
        />
        {isSelected ? (
          <Check className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-green-500" />
        ) : (
          <Building2 className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
        )}
      </div>

      {/* Dropdown */}
      {open && filteredClients.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-lg overflow-hidden"
        >
          <div className="p-1 max-h-[200px] overflow-y-auto">
            {filteredClients.map((client) => (
              <button
                key={client.id}
                type="button"
                onClick={() => handleSelect(client)}
                className={cn(
                  "w-full flex items-center gap-2 px-3 py-2 text-left rounded-md transition-colors",
                  "hover:bg-zinc-800 text-zinc-300 hover:text-zinc-50",
                  selectedId === client.id && "bg-zinc-800 text-green-400"
                )}
              >
                <User className="h-4 w-4 text-zinc-500 flex-shrink-0" />
                <span className="truncate">{client.name}</span>
                {selectedId === client.id && (
                  <Check className="h-4 w-4 text-green-500 ml-auto" />
                )}
              </button>
            ))}
          </div>
          {inputValue && !filteredClients.some(c => c.name.toLowerCase() === inputValue.toLowerCase()) && (
            <div className="px-3 py-2 text-xs text-zinc-500 border-t border-zinc-800">
              Press Enter to use "{inputValue}" as new client
            </div>
          )}
        </div>
      )}

      {/* Helper text */}
      {isSelected && (
        <p className="text-xs text-green-500 mt-1">
          ✓ Existing client selected
        </p>
      )}
    </div>
  );
};

export default ClientCombobox;
